/**
 * Internal image-review page (route /_dev/images).
 *
 * Lists all 140 word images in a 4-column grid with the word + meaning
 * underneath each. Not linked from anywhere in the UI — reach it via the
 * URL. Used to eyeball the Flux generations for obvious misses so we
 * can flag them for regeneration.
 *
 * The page also has a "Copy flag list" button that collects every image
 * the reviewer marks as weak and outputs a comma-separated list of ids
 * ready to pass to `generate-images -- --only=X,Y,Z`.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../lib/api';
import type { Word } from '@huroof/shared';
import type { LetterSummary } from '../state/content';
import { Spinner } from '../components/Spinner';

interface WordWithLetter extends Word {
  letter: string;
}

export function DevImages() {
  const { route } = useLocation();
  const [words, setWords] = useState<WordWithLetter[]>([]);
  const [flagged, setFlagged] = useState<Set<number>>(new Set());

  useEffect(() => {
    (async () => {
      const lResp = await api<{ ok: true; letters: LetterSummary[] }>(
        '/api/letters',
      );
      const all: WordWithLetter[] = [];
      for (const l of lResp.letters) {
        const wResp = await api<{ ok: true; words: Word[] }>(
          `/api/letters/${l.id}/words`,
        );
        for (const w of wResp.words) all.push({ ...w, letter: l.letter });
      }
      setWords(all);
    })();
  }, []);

  function toggle(id: number) {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function copyList() {
    const list = [...flagged].sort((a, b) => a - b).join(',');
    navigator.clipboard?.writeText(list).catch(() => undefined);
    alert(
      `Flagged image ids copied to clipboard:\n${list}\n\nRegen:\n  pnpm --filter @huroof/content-gen generate:images -- --only=${list}`,
    );
  }

  if (words.length === 0) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner label="جَارٍ الاِسْتِعْرَاضُ" labelEn="Loading all images…" />
      </div>
    );
  }

  return (
    <div class="flex-1 px-4 py-6 pb-24 max-w-5xl mx-auto w-full" dir="ltr">
      <header class="flex items-center justify-between mb-4 sticky top-0 bg-sand/95 backdrop-blur py-2 z-10">
        <button
          type="button"
          onClick={() => route('/map')}
          class="text-sm text-ink/60 underline"
        >
          ← back
        </button>
        <div class="text-sm text-ink/70 font-mono">
          {words.length} images · {flagged.size} flagged
        </div>
        <button
          type="button"
          onClick={copyList}
          disabled={flagged.size === 0}
          class="px-3 py-1 rounded-lg bg-coral text-white text-sm disabled:opacity-40"
        >
          Copy flag list
        </button>
      </header>

      <p class="text-xs text-ink/50 mb-3">
        Click an image to flag it as weak (red border). Shift-click to pre-play
        the word audio. Run{' '}
        <code class="bg-ink/10 px-1 py-0.5 rounded">
          pnpm --filter @huroof/content-gen generate:images -- --only=...
        </code>{' '}
        with the copied list.
      </p>

      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {words.map((w) => {
          const isFlagged = flagged.has(w.id);
          return (
            <button
              key={w.id}
              type="button"
              class={`relative rounded-2xl overflow-hidden bg-white shadow text-left active:scale-95 transition ${
                isFlagged ? 'ring-4 ring-berry' : 'ring-1 ring-ink/10'
              }`}
              onClick={(e) => {
                if ((e as MouseEvent).shiftKey) {
                  new Audio(w.audio_url).play().catch(() => undefined);
                  return;
                }
                toggle(w.id);
              }}
            >
              <img
                src={w.image_url}
                alt=""
                loading="lazy"
                class="w-full aspect-square object-cover bg-sand"
              />
              <div class="p-2">
                <div class="flex items-center justify-between text-xs text-ink/60 mb-1">
                  <span class="font-mono">#{w.id}</span>
                  <span class="text-lg font-display text-coral">{w.letter}</span>
                </div>
                <div class="font-display text-lg leading-tight" dir="rtl">
                  {w.word_ar}
                </div>
                <div class="text-[11px] text-ink/60 truncate">
                  {w.meaning_en}
                </div>
              </div>
              {isFlagged && (
                <span class="absolute top-1 left-1 text-xs bg-berry text-white rounded-full px-2 py-0.5">
                  flagged
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
